import React, { useState } from "react";
import { Button, Offcanvas } from "react-bootstrap";
import { useSelector } from "react-redux";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import timezones from "../Builder/nodes/Logic/timezones";
import CogIcon from "../../assets/icons/cog.svg2";
import {
  selectDataCategories,
  selectInputArguments,
  selectRawDataCategories,
} from "../store/slices/projectSlice";
import {
  useGetApiDataCollectionQuery,
  useGetDataEntryStructureQuery,
} from "../store/slices/settingsSlice";

export default function AdvancedFilter({
  contactFields,
  filter,
  setFilter,
  isRuntimeContacts,
}) {
  const { countries, defaultTZ } = JSON.parse(
    document.querySelector("#app").dataset.page
  ).props;
  const contactField = contactFields.find((cf) => cf.uuid === filter.fields[0]);
  const dataCategories = [
    ...useSelector(selectDataCategories),
    ...useSelector(selectRawDataCategories),
  ];
  const inputArguments = useSelector(selectInputArguments);
  const selectedVariable = inputArguments.find(
    (ia) => ia.UUID === filter.valueId
  );
  const { data: selectedDataStructure } = useGetApiDataCollectionQuery(
    selectedVariable?.DataStructure,
    {
      skip:
        !["json"].includes(selectedVariable?.Format) ||
        !selectedVariable?.DataStructure,
    }
  );

  const { data: selectedDataEntryStructure } = useGetDataEntryStructureQuery(
    selectedVariable?.DataStructure,
    {
      skip:
        !["data-entry"].includes(selectedVariable?.Format) ||
        !selectedVariable?.DataStructure,
    }
  );

  const [showSidebar, setShowSidebar] = useState(false);

  return (
    <div className="row g-3 align-items-end">
      {filter.order > 0 && (
        <div className="col-1">
          <Input
            label="And/Or"
            inputType="select"
            isValidNewOption={() => false}
            options={[
              { value: false, label: "and" },
              { value: true, label: "or" },
            ]}
            value={[filter.isOr]}
            onChange={(values) => setFilter({ ...filter, isOr: values[0] })}
          />
        </div>
      )}

      <div className={`${filter.order > 0 ? "col-2" : "col-3"}`}>
        <Input
          label="Field"
          inputType="select"
          isValidNewOption={() => false}
          options={[
            { value: "status", label: "System status" },
            ...(isRuntimeContacts
              ? [
                  { value: "asset", label: "Associated to asset" },
                  { value: "event", label: "Associated to event" },
                  { value: "group", label: "Associated to group" },
                  { value: "location", label: "System location" },
                  { value: "variable", label: "Contacts in variable" },
                  {
                    value: "group-variable",
                    label: "Contacts in group variable",
                  },
                ]
              : []),
            ...contactFields
              .filter((cf) => !cf.isHidden)
              .map((cf) => ({
                value: cf.uuid,
                label: cf.label,
              })),
          ]}
          value={filter.fields}
          onChange={(values) =>
            setFilter({
              ...filter,
              fields: values,
              operator: "is",
              valueType: [
                "location",
                "asset",
                "variable",
                "event",
                "group-variable",
              ].includes(values[0])
                ? "MainThread"
                : "fixed",
              values: [],
              valueId: "",
            })
          }
        />
      </div>

      <div className="col-2">
        <Input
          key={filter.fields}
          label="Operator"
          inputType="select"
          isValidNewOption={() => false}
          options={[
            ...(![
              "location",
              "group",
              "group-variable",
              "asset",
              "event",
              "variable",
            ].includes(filter.fields[0])
              ? [
                  { value: "empty", label: "empty" },
                  { value: "not-empty", label: "not-empty" },
                ]
              : []),
            { value: "is", label: "is" },
            { value: "not", label: "is not" },
            ...(!(
              contactField?.isSelect ||
              filter.fields[0] === "status" ||
              filter.fields[0] === "location" ||
              filter.fields[0] === "asset" ||
              filter.fields[0] === "event" ||
              filter.fields[0] === "group" ||
              filter.fields[0] === "variable" ||
              filter.fields[0] === "group-variable" ||
              contactField?.isDateTime
            )
              ? [
                  { value: "contains", label: "contains" },
                  { value: "prefix", label: "prefix" },
                  { value: "suffix", label: "suffix" },
                  { value: "not-suffix", label: "not-suffix" },
                  { value: "not-prefix", label: "not-prefix" },
                  { value: "not-contains", label: "not-contains" },
                ]
              : []),
            // eslint-disable-next-line no-nested-ternary
            ...(contactField?.isDateTime
              ? contactField?.isMultiple
                ? [
                    { value: "before-any", label: "any value before" },
                    { value: "before-all", label: "all values before" },
                    { value: "after-any", label: "any value after" },
                    { value: "after-all", label: "all values after" },
                  ]
                : [
                    { value: "before-any", label: "before" },
                    { value: "after-any", label: "after" },
                  ]
              : []),
          ]}
          value={[filter.operator]}
          onChange={(values) => setFilter({ ...filter, operator: values[0] })}
        />
      </div>

      {!["empty", "not-empty"].includes(filter.operator) &&
        isRuntimeContacts &&
        filter.fields[0] !== "group" && (
          <div className="col-3">
            <Input
              label="Value type"
              maxLength={255}
              value={[filter.valueType]}
              inputType="select"
              onChange={(values) =>
                setFilter({
                  ...filter,
                  valueType: values[0],
                  valueId: "",
                  values: [],
                })
              }
              options={[
                // add now to options only if time based field
                ...(isRuntimeContacts && contactField?.isDateTime
                  ? [
                      {
                        value: "now",
                        label: `Current ${
                          contactField.dateTimeType.toLowerCase() == "datetime"
                            ? "date and time"
                            : contactField.dateTimeType
                        }`,
                      },
                    ]
                  : []),
                ...[
                  ...(!["location", "asset", "event", "variable"].includes(
                    filter.fields[0]
                  )
                    ? [{ value: "fixed", label: "Fixed value" }]
                    : []),
                  { value: "MainThread", label: "Variable on Main thread" },
                  {
                    value: "CurrentThread",
                    label: "Variable on Current thread",
                  },
                ],
              ]}
            />
          </div>
        )}

      <div className="col d-flex align-items-end">
        <div className="flex-grow-1">
          {["MainThread", "CurrentThread"].includes(filter.valueType) && (
            <Input
              key={filter.fields + filter.valueType}
              label="Value"
              inputType="select"
              isValidNewOption={() => false}
              maxLength={255}
              value={[filter.valueId]}
              onChange={(values) =>
                setFilter({ ...filter, valueId: values[0], values: [] })
              }
              options={inputArguments
                .filter((arg) => {
                  if (
                    contactField?.isDateTime &&
                    arg.Format !== contactField?.dateTimeType
                  ) {
                    return false;
                  }
                  if (
                    ["location"].includes(filter.fields[0]) &&
                    arg.Format !== "geo-json"
                  ) {
                    return false;
                  }
                  if (
                    ["asset"].includes(filter.fields[0]) &&
                    arg.Format !== "asset"
                  ) {
                    return false;
                  }
                  if (
                    ["event"].includes(filter.fields[0]) &&
                    arg.Format !== "event"
                  ) {
                    return false;
                  }
                  if (
                    ["variable"].includes(filter.fields[0]) &&
                    arg.Format !== "contact"
                  ) {
                    return false;
                  }
                  if (
                    ["group-variable"].includes(filter.fields[0]) &&
                    arg.Format !== "group"
                  ) {
                    return false;
                  }
                  if (
                    ["status"].includes(filter.fields[0]) &&
                    arg.Format !== "short-text"
                  ) {
                    return false;
                  }
                  if (
                    ![
                      "asset",
                      "location",
                      "variable",
                      "event",
                      "group-variable",
                    ].includes(filter.fields[0]) &&
                    (["asset", "contact", "event"].includes(arg.Format) ||
                      (["json"].includes(arg.Format) && !arg.DataStructure))
                  ) {
                    return false;
                  }
                  if (arg.Format === "binary") {
                    return false;
                  }

                  return true;
                })
                .map((arg) => ({
                  value: arg.UUID,
                  label: arg.Label,
                }))}
            />
          )}

          {(filter.valueType === "fixed" || !isRuntimeContacts) &&
            !["empty", "not-empty", "now"].includes(filter.operator) &&
            !contactField?.isDateTime &&
            filter.fields[0] !== "group" && (
              <Input
                key={filter.fields + filter.valueType}
                label="Value"
                isMulti
                maxLength={255}
                value={filter.values}
                inputType={contactField?.isSelect ? "select" : undefined}
                isValidNewOption={(v) =>
                  v && !contactField?.isSelect && filter.fields[0] !== "status"
                }
                onChange={(values) => setFilter({ ...filter, values })}
                options={[
                  ...(!contactField?.selectType
                    ? (contactField?.options || []).map((o) => ({
                        value: o.uuid,
                        label: o.label,
                      }))
                    : []),
                  ...(contactField?.selectType === "country"
                    ? countries.map((c) => ({
                        value: c.country_code,
                        label: c.country_name,
                      }))
                    : []),
                  ...(contactField?.selectType === "time-zone"
                    ? timezones.map((tz) => ({
                        value: tz.tzCode,
                        label: tz.label,
                      }))
                    : []),
                  ...(filter.fields[0] === "status"
                    ? [
                        { value: "active", label: "active" },
                        { value: "incomplete", label: "incomplete" },
                        { value: "archived", label: "archived" },
                      ]
                    : []),
                ]}
              />
            )}

          {filter.fields[0] === "group" && (
            <Input
              key={filter.fields[0] + filter.valueType + "contactGroup"}
              label="Value"
              inputType="contactSet"
              isMultiple
              isValidNewOption={() => false}
              value={filter.values}
              onChange={(values) => setFilter({ ...filter, values })}
            />
          )}

          {contactField?.isDateTime && filter.valueType === "fixed" && (
            <div className="col">
              <Input
                label="Select date/time"
                inputType="dateTime"
                dateTimeType={contactField?.dateTimeType}
                isDateTimeUTC={filter.timeZoneFrom === "fixed"}
                value={filter.values}
                onChange={(values) => setFilter({ ...filter, values })}
              />
            </div>
          )}
        </div>
        {selectedVariable?.Format === "json" &&
          selectedVariable?.DataStructure && (
            <div className="w-50">
              {["MainThread", "CurrentThread"].includes(filter.valueType) && (
                <Input
                  key={filter.fields + filter.value}
                  label="Data field"
                  inputType="select"
                  isValidNewOption={() => false}
                  maxLength={255}
                  value={filter.valueOptions || []}
                  onChange={(valueOptions) =>
                    setFilter({ ...filter, valueOptions })
                  }
                  options={selectedDataStructure?.Fields?.map((field) => ({
                    value: field.Path,
                    label: field.Name,
                  }))}
                />
              )}
            </div>
          )}
        {selectedVariable?.Format === "data-entry" &&
          selectedVariable?.DataStructure && (
            <div className="w-50">
              {["MainThread", "CurrentThread"].includes(filter.valueType) && (
                <Input
                  key={filter.fields + filter.value}
                  label="Data field"
                  inputType="select"
                  isValidNewOption={() => false}
                  maxLength={255}
                  value={filter.valueOptions || []}
                  onChange={(valueOptions) =>
                    setFilter({ ...filter, valueOptions })
                  }
                  options={selectedDataEntryStructure?.Fields?.map((field) => ({
                    value: field.Path,
                    label: field.Name,
                  }))}
                />
              )}
            </div>
          )}

        {contactField?.isDateTime && isRuntimeContacts && (
          <>
            <Button
              className="ms-2 mb-1"
              size="sm"
              variant=""
              onClick={() => setShowSidebar(true)}
            >
              <CogIcon width={20} className="purple" />
            </Button>

            <Offcanvas
              show={showSidebar}
              placement="end"
              onHide={() => setShowSidebar(false)}
            >
              <Offcanvas.Header closeButton>
                <Offcanvas.Title>Date/time options</Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body>
                <div className="mb-3">
                  <Input
                    label="Offset from"
                    maxLength={255}
                    value={[filter.offsetFrom]}
                    inputType="select"
                    onChange={(values) =>
                      setFilter({
                        ...filter,
                        offsetFrom: values[0],
                        offset:
                          values[0] === "fixed"
                            ? "0"
                            : inputArguments.find((arg) =>
                                ["number"].includes(arg.Format)
                              )?.UUID,
                      })
                    }
                    options={[
                      { value: "fixed", label: "Fixed value" },
                      {
                        value: "MainThread",
                        label: "Variable on Main thread",
                      },
                      {
                        value: "CurrentThread",
                        label: "Variable on Current thread",
                      },
                    ]}
                  />
                </div>

                {["MainThread", "CurrentThread"].includes(
                  filter.offsetFrom
                ) && (
                  <div className="mb-3">
                    <Input
                      key={filter.fields + filter.offsetFrom}
                      label="Offset value"
                      inputType="select"
                      isValidNewOption={() => false}
                      maxLength={255}
                      value={[filter.offset]}
                      onChange={(values) =>
                        setFilter({ ...filter, offset: values[0] })
                      }
                      options={inputArguments
                        .filter((arg) => ["number"].includes(arg.Format))
                        .map((arg) => ({
                          value: arg.UUID,
                          label: arg.Label,
                        }))}
                    />
                  </div>
                )}

                {(filter.offsetFrom === "fixed" || !isRuntimeContacts) &&
                  !["empty", "not-empty"].includes(filter.operator) && (
                    <div className="mb-3">
                      <Input
                        key={filter.fields + filter.offsetFrom}
                        label="Offset value"
                        maxLength={255}
                        value={[String(filter.offset)]}
                        onChange={(values) =>
                          setFilter({
                            ...filter,
                            offset: String(values[0]),
                          })
                        }
                      />
                    </div>
                  )}

                <div className="mb-3">
                  <Input
                    label="Offset unit"
                    maxLength={255}
                    value={[filter.offsetUnit ? filter.offsetUnit : "day"]}
                    inputType="select"
                    onChange={(values) =>
                      setFilter({
                        ...filter,
                        offsetUnit: values[0],
                      })
                    }
                    options={[
                      { value: "minute", label: "Minute" },
                      { value: "hour", label: "Hour" },
                      { value: "day", label: "Day" },
                      { value: "month", label: "Month" },
                    ]}
                  />
                </div>
                {filter.offsetFrom === "fixed" && (
                  <div className="mb-3">
                    <Input
                      label="Offset type"
                      maxLength={255}
                      value={[
                        filter.offsetType ? filter.offsetType : "positive",
                      ]}
                      inputType="select"
                      onChange={(values) =>
                        setFilter({
                          ...filter,
                          offsetType: values[0],
                        })
                      }
                      options={[
                        { value: "positive", label: "Positive" },
                        { value: "negative", label: "Negative" },
                      ]}
                    />
                  </div>
                )}

                <div className="mb-3">
                  <Input
                    label="Time zone from"
                    maxLength={255}
                    value={[filter.timeZoneFrom]}
                    inputType="select"
                    onChange={(values) =>
                      setFilter({
                        ...filter,
                        timeZoneFrom: values[0],
                        timeZone:
                          values[0] === "fixed"
                            ? ""
                            : inputArguments.find((arg) =>
                                ["short-text"].includes(arg.Format)
                              )?.UUID ?? "",
                      })
                    }
                    options={[
                      { value: "fixed", label: "Fixed value" },
                      {
                        value: "MainThread",
                        label: "Variable on Main thread",
                      },
                      {
                        value: "CurrentThread",
                        label: "Variable on Current thread",
                      },
                    ]}
                  />
                </div>

                {["MainThread", "CurrentThread"].includes(
                  filter.timeZoneFrom
                ) && (
                  <div className="mb-3">
                    <Input
                      key={filter.fields + filter.timeZoneFrom}
                      label="Time zone value"
                      inputType="select"
                      isValidNewOption={() => false}
                      maxLength={255}
                      value={[filter.timeZone]}
                      onChange={(values) =>
                        setFilter({ ...filter, timeZone: values[0] })
                      }
                      options={inputArguments
                        .filter((arg) => ["short-text"].includes(arg.Format))
                        .map((arg) => ({
                          value: arg.UUID,
                          label: arg.Label,
                        }))}
                    />
                  </div>
                )}

                {filter.timeZoneFrom === "fixed" &&
                  !["empty", "not-empty"].includes(filter.operator) && (
                    <div className="mb-3">
                      <Input
                        key={filter.fields + filter.timeZoneFrom}
                        label="Time zone value"
                        maxLength={255}
                        value={[filter.timeZone]}
                        onChange={(values) =>
                          setFilter({ ...filter, timeZone: values[0] })
                        }
                        inputType="select"
                        options={[
                          {
                            value: "",
                            label: `Default from product settings: ${defaultTZ}`,
                          },
                          ...timezones.map((tz) => ({
                            value: tz.tzCode,
                            label: tz.label,
                          })),
                        ]}
                      />
                    </div>
                  )}
              </Offcanvas.Body>
            </Offcanvas>
          </>
        )}
      </div>
    </div>
  );
}
